import { useState, useEffect, useRef, useCallback, MutableRefObject } from 'react'

interface TimerProps {
	seconds: number
	onStart?: () => void
	onEnd?: () => void
	timeOut?: number
}

export interface TimerState {
	toggleTimerPlaying: (option?: boolean) => void
	timerRef: MutableRefObject<NodeJS.Timeout | null>
	isTimerPlaying: boolean
	remainingTime: number
	addTime: (time?: number) => void
}

export const useTimer = ({ seconds, onStart, onEnd, timeOut }: TimerProps): TimerState => {
	const [isTimerPlaying, setIsTimerPlaying] = useState<boolean>(false)
	const [remainingTime, setRemainingTime] = useState<number>(seconds)
	const remainingTimeRef = useRef<number>(seconds)
	const timerRef = useRef<NodeJS.Timeout | null>(null)
	const isEndCalled = useRef<boolean>(false)
	const onStartRef = useRef(onStart)
	const onEndRef = useRef(onEnd)

	useEffect(() => {
		onStartRef.current = onStart
	}, [onStart])

	useEffect(() => {
		onEndRef.current = onEnd
	}, [onEnd])

	const clearTimer = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current)
			timerRef.current = null
		}
	}, [])

	const completeTimer = useCallback((syncRemainingTime = true) => {
		clearTimer()
		setIsTimerPlaying(false)
		remainingTimeRef.current = 0
		if (syncRemainingTime) {
			setRemainingTime(0)
		}
		if (onEndRef.current && !isEndCalled.current) {
			isEndCalled.current = true
			onEndRef.current()
		}
	}, [clearTimer])

	useEffect(() => {
		clearTimer()
		isEndCalled.current = false
		remainingTimeRef.current = Math.max(0, seconds)
		setIsTimerPlaying(false)
		setRemainingTime(remainingTimeRef.current)
	}, [seconds, clearTimer])

	useEffect(() => {
		if (isTimerPlaying) {
			if (onStartRef.current) onStartRef.current()
			timerRef.current = setInterval(
				() => {
					const nextTime = Math.max(
						0,
						remainingTimeRef.current - (timeOut ? timeOut / 1000 : 0.1)
					)
					remainingTimeRef.current = nextTime
					setRemainingTime(nextTime)
					if (nextTime <= 0) {
						completeTimer(false)
					}
				},
				timeOut ? timeOut : 100
			)
		} else {
			clearTimer()
		}

		return clearTimer
	}, [isTimerPlaying, timeOut, clearTimer, completeTimer])

	const toggleTimerPlaying = useCallback((option?: boolean) => {
		if (typeof option === 'boolean') {
			setIsTimerPlaying(option)
			return
		}
		setIsTimerPlaying(prevPlaying => !prevPlaying)
	}, [])

	const addTime = useCallback(
		(time?: number) => {
			const nextTime = Math.max(0, remainingTimeRef.current + (time ?? 20))
			remainingTimeRef.current = nextTime
			setRemainingTime(nextTime)
			if (nextTime > 0) {
				isEndCalled.current = false
				return
			}
			completeTimer(false)
		},
		[completeTimer]
	)

	return {
		isTimerPlaying,
		remainingTime,
		timerRef,
		toggleTimerPlaying,
		addTime,
	}
}
